import type { AgendaSlotStatus } from "~/ui-rtk/api/agendaApi"

/**
 * Linear-style status glyphs: dashed circle (todo), partial pie
 * (in progress / in review), filled check (done), etc. Inline SVG so they
 * render crisply at chip size with no icon-font dependency.
 */

export const STATUS_COLORS: Record<AgendaSlotStatus, string> = {
  TODO: "#8A94A6",
  IN_PROGRESS: "#38BDF8",
  IN_REVIEW: "#1FB2A5",
  BLOCKED: "#F59E0B",
  DONE: "#22C55E",
  CANCELLED: "#94A3B8",
}

export default function StatusIcon({
  status,
  size = 13,
  className,
}: {
  status: AgendaSlotStatus
  size?: number
  className?: string
}) {
  const color = STATUS_COLORS[status]
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    className,
    "aria-label": status,
  }

  switch (status) {
    case "TODO":
      return (
        <svg {...common}>
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
            strokeDasharray="2.5 2"
          />
        </svg>
      )
    case "IN_PROGRESS":
      return (
        <svg {...common}>
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
          />
          {/* right half pie */}
          <path d="M8 4 A4 4 0 0 1 8 12 Z" fill={color} />
        </svg>
      )
    case "IN_REVIEW":
      return (
        <svg {...common}>
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
          />
          {/* three-quarter pie */}
          <path d="M8 4 A4 4 0 1 1 4 8 L8 8 Z" fill={color} />
        </svg>
      )
    case "BLOCKED":
      return (
        <svg {...common}>
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
          />
          <rect
            x="7.3"
            y="4.5"
            width="1.4"
            height="4.4"
            rx="0.7"
            fill={color}
          />
          <circle cx="8" cy="11" r="0.9" fill={color} />
        </svg>
      )
    case "DONE":
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="6.6" fill={color} />
          <path
            d="M5.2 8.2l1.9 1.9 3.7-4.1"
            fill="none"
            stroke="#0B1120"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "CANCELLED":
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="6.6" fill={color} />
          <path
            d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4"
            stroke="#0B1120"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      )
    default:
      return null
  }
}
